import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, ChevronDown, Globe, Sparkles, Filter, Layers,
  ClipboardCheck, TrendingUp, Monitor, MessageSquare, Building2, X,
  Send, Loader2, PhoneCall, FileText, QrCode, GitCompare, Bookmark,
} from "lucide-react";
import api from "@/lib/api";
import { LANDING_I18N, LANDING_LANGS } from "@/lib/landingI18n";
import useDocumentLocale from "@/hooks/useDocumentLocale";
import useDialogFocus from "@/hooks/useDialogFocus";

/**
 * Landing page — Interactive Real Estate Sales Platform (Iter32).
 *
 * Sales-focused positioning. NO technical language about renders / videos /
 * homography / polygons / 3D. Copy in EN / AR / TR (RTL for Arabic).
 *
 * Palette:
 *   background   #0B0E14  (deep navy-charcoal)
 *   panel        #10141B
 *   line         rgba(255,255,255,0.08)
 *   accent       #C4A47C  champagne / gold
 *   accent hi    #E0C594
 *   text         #F5F5F1 / rgba(255,255,255,0.66)
 */
const LS_KEY = "projevista-lang";

export default function Landing() {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    const preferred = localStorage.getItem(LS_KEY) || navigator.language?.slice(0, 2);
    return LANDING_LANGS.some(l => l.code === preferred) ? preferred : "en";
  });
  const meta = LANDING_LANGS.find((l) => l.code === lang) || LANDING_LANGS[0];
  const t = LANDING_I18N[lang] || LANDING_I18N.en;
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoPlan, setDemoPlan] = useState(null);
  const [demoSlug, setDemoSlug] = useState("");

  useDocumentLocale(meta.code, t.ui.title);
  useEffect(() => { localStorage.setItem(LS_KEY, meta.code); }, [meta.code]);

  useEffect(() => {
    // Load the admin-configured "official demo" project for the Explore Demo CTA.
    (async () => {
      try {
        const r = await api.get("/marketing/site-settings");
        if (r?.data?.official_demo_slug) setDemoSlug(r.data.official_demo_slug);
      } catch (_) { /* no published demo yet — CTA links stay hidden */ }
    })();
  }, []);

  const openDemo = (planId = null) => { setDemoPlan(planId); setDemoOpen(true); };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-[#F5F5F1] antialiased selection:bg-[#C4A47C]/40 selection:text-[#0B0E14]" data-testid="landing-page">
      <style>{`
        .fnt-display { font-family: "Fraunces", "Playfair Display", Georgia, serif; letter-spacing: -0.01em; }
        .eyebrow    { color: #C4A47C; font-size: 11px; letter-spacing: 0.35em; text-transform: uppercase; }
        .gold       { color: #C4A47C; }
        .btn-primary   { background:#C4A47C; color:#0B0E14; }
        .btn-primary:hover { background:#E0C594; }
        .btn-ghost     { background:transparent; color:#F5F5F1; border:1px solid rgba(255,255,255,0.18); }
        .btn-ghost:hover { background: rgba(255,255,255,0.06); }
        .card-glass    { background: rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); }
        .grain::before { content: ""; position: absolute; inset:0; pointer-events:none;
                         background-image: radial-gradient(rgba(255,255,255,0.06) 0.5px, transparent 0.5px);
                         background-size: 3px 3px; opacity: 0.35; mix-blend-mode: overlay;}
        .halo          { background: radial-gradient(60% 50% at 50% 0%, rgba(196,164,124,0.18) 0%, rgba(196,164,124,0) 60%); }
        .rtl input, .rtl textarea, .rtl select { text-align: right; }
        [dir="rtl"] .flip-icon { transform: scaleX(-1); }
        @keyframes fadeUp { from { opacity:0; transform: translateY(12px);} to {opacity:1; transform: none;} }
        .rise > * { animation: fadeUp 700ms ease-out both; }
        .rise > *:nth-child(1){ animation-delay: 0.02s;}
        .rise > *:nth-child(2){ animation-delay: 0.12s;}
        .rise > *:nth-child(3){ animation-delay: 0.22s;}
        .rise > *:nth-child(4){ animation-delay: 0.32s;}
      `}</style>

      <TopBar t={t} meta={meta} setLang={setLang} onBook={() => openDemo()} demoSlug={demoSlug} />
      <Hero t={t} onBook={() => openDemo()} demoSlug={demoSlug} />
      <Problem t={t} />
      <Immersive t={t} demoSlug={demoSlug} />
      <SmartSelection t={t} />
      <SalesTools t={t} />
      <Leads t={t} />
      <Analytics t={t} />
      <Showroom t={t} />
      <Pricing t={t} lang={meta.code} onChoose={(id) => openDemo(id)} />
      <FinalCta t={t} onBook={() => openDemo()} />
      <Footer t={t} />

      {demoOpen && (
        <BookDemoModal t={t} lang={lang} initialPlan={demoPlan} onClose={() => setDemoOpen(false)} />
      )}
    </div>
  );
}

// ── Top bar ────────────────────────────────────────────────────────────────
function TopBar({ t, meta, setLang, onBook, demoSlug }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 bg-[#0B0E14]/85 backdrop-blur border-b border-white/5">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        <div className="flex items-center gap-2.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[#C4A47C]" />
          <span className="fnt-display text-lg tracking-tight" data-testid="brand-name">Projevista</span>
        </div>
        <nav className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setOpen((x) => !x)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs uppercase tracking-wider hover:bg-white/5"
              data-testid="landing-lang-toggle" aria-haspopup="listbox" aria-expanded={open}>
              <Globe className="h-3.5 w-3.5" /> {meta.short}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {open && (
              <div className="absolute end-0 mt-2 min-w-[160px] rounded-lg border border-white/10 bg-[#10141B] shadow-2xl py-1" onMouseLeave={() => setOpen(false)}>
                {LANDING_LANGS.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
                    className="w-full text-start px-3 py-2 text-sm hover:bg-white/5" data-testid={`landing-lang-${l.code}`}>
                    <span className="gold text-[10px] tracking-wider me-2">{l.short}</span> {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {demoSlug && (
            <Link to={`/p/${demoSlug}`} data-testid="nav-open-demo"
              className="hidden md:inline-block px-4 py-2 rounded-full btn-ghost text-xs uppercase tracking-wider">
              {t.nav.explore}
            </Link>
          )}
          <Link to="/login" data-testid="nav-sign-in"
            className="hidden md:inline-block px-4 py-2 rounded-full btn-ghost text-xs uppercase tracking-wider">
            {t.nav.signIn}
          </Link>
          <button onClick={onBook} data-testid="nav-book-demo"
            className="px-4 py-2 rounded-full btn-primary text-xs uppercase tracking-wider">
            {t.nav.bookDemo}
          </button>
        </nav>
      </div>
    </header>
  );
}

// ── Hero ───────────────────────────────────────────────────────────────────
function Hero({ t, onBook, demoSlug }) {
  return (
    <section className="relative overflow-hidden" data-testid="landing-hero">
      <div className="absolute inset-0 halo pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] items-center gap-14">
        <div className="rise">
          <div className="eyebrow" data-testid="landing-hero-eyebrow">{t.hero.eyebrow}</div>
          <h1 className="fnt-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-4 max-w-[720px]"
            data-testid="landing-hero-headline">
            {t.hero.headline}
          </h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-[600px] leading-relaxed">
            {t.hero.sub}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <button onClick={onBook} data-testid="hero-book-demo"
              className="btn-primary px-6 py-3 rounded-full text-sm font-medium tracking-wider uppercase inline-flex items-center gap-2">
              {t.hero.primary} <ArrowRight className="h-4 w-4 flip-icon" />
            </button>
            {demoSlug && (
              <Link to={`/p/${demoSlug}`} data-testid="hero-explore-demo"
                className="btn-ghost px-6 py-3 rounded-full text-sm font-medium tracking-wider uppercase inline-flex items-center gap-2">
                {t.hero.secondary}
              </Link>
            )}
          </div>
          <div className="mt-8 text-[11px] uppercase tracking-[0.3em] text-white/40">{t.hero.ipad}</div>
        </div>
        <div className="relative rise">
          {/* iPad / showroom mock */}
          <div className="relative aspect-[16/11] rounded-[28px] border border-[#C4A47C]/25 bg-gradient-to-br from-[#10141B] to-[#0B0E14] shadow-[0_40px_120px_rgba(196,164,124,0.15)] overflow-hidden">
            <div className="absolute inset-x-4 top-4 h-10 rounded-2xl bg-white/[0.03] border border-white/5 flex items-center px-4 gap-2 text-[10px] uppercase tracking-[0.3em] text-white/40">
              <div className="h-1.5 w-1.5 rounded-full bg-emerald-400/70" />
              <bdi lang="en" dir="ltr" className="normal-case">projevista.com / horizon-residences</bdi>
            </div>
            <div className="absolute inset-x-4 top-16 bottom-4 rounded-2xl overflow-hidden bg-black">
              <video
                className="absolute inset-0 w-full h-full object-cover"
                src="/videos/overview.mp4" poster="/videos/overview.jpg"
                autoPlay muted loop playsInline preload="metadata"
                data-testid="landing-video-overview"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/50 to-transparent" />
              <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-3">
                <div className="card-glass rounded-xl p-3 max-w-[62%] backdrop-blur-md">
                  <div className="text-[10px] uppercase tracking-[0.3em] gold">{t.ui.available} · {t.ui.sea_view}</div>
                  <div className="fnt-display text-lg mt-1"><bdi dir="ltr">A-702 · 2+1</bdi></div>
                  <div className="text-xs text-white/70 mt-1"><bdi dir="ltr">104 m²</bdi> · {t.ui.floor} <bdi>7</bdi> · <bdi dir="ltr">$242,000</bdi></div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="card-glass rounded-full px-3 py-1.5 text-[10px] uppercase tracking-wider backdrop-blur-md inline-flex items-center gap-1"><Filter className="h-3 w-3" /> {t.ui.matches}</div>
                  <div className="card-glass rounded-full px-3 py-1.5 text-[10px] uppercase tracking-wider backdrop-blur-md inline-flex items-center gap-1"><Bookmark className="h-3 w-3" /> {t.ui.shortlisted}</div>
                </div>
              </div>
            </div>
          </div>
          {/* Floating chips */}
          <div className="absolute -left-4 md:-left-8 top-10 card-glass backdrop-blur-md rounded-2xl p-3 shadow-2xl hidden md:block">
            <div className="text-[10px] uppercase tracking-[0.3em] gold">{t.ui.payment_plan}</div>
            <div className="text-sm mt-1"><bdi>{t.ui.payment_example}</bdi></div>
          </div>
          <div className="absolute -right-4 md:-right-8 -bottom-6 card-glass backdrop-blur-md rounded-2xl p-3 shadow-2xl hidden md:block">
            <div className="text-[10px] uppercase tracking-[0.3em] gold">{t.ui.pdf_offer}</div>
            <div className="text-sm mt-1 inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> <bdi lang="en" dir="ltr">Horizon_Offer_A-702.pdf</bdi></div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Problem ────────────────────────────────────────────────────────────────
function Problem({ t }) {
  return (
    <Section id="problem" testid="landing-problem" eyebrow={t.problem.eyebrow} title={t.problem.title} sub={t.problem.sub}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
        {t.problem.cards.map((c, i) => (
          <div key={i} className="card-glass rounded-2xl p-5 hover:border-[#C4A47C]/25 transition">
            <div className="fnt-display text-lg leading-snug">{c.t}</div>
            <div className="text-white/60 text-sm mt-2 leading-relaxed">{c.d}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── Immersive ──────────────────────────────────────────────────────────────
function Immersive({ t, demoSlug }) {
  return (
    <Section id="immersive" testid="landing-immersive" eyebrow={t.immersive.eyebrow} title={t.immersive.title} sub={t.immersive.sub}>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10 bg-black">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/videos/units.mp4" poster="/videos/units.jpg"
            autoPlay muted loop playsInline preload="metadata"
            data-testid="landing-video-units"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex flex-wrap gap-2">
            {t.immersive.bullets.map((b) => (
              <span key={b} className="card-glass backdrop-blur-md rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider">{b}</span>
            ))}
          </div>
        </div>
        <div>
          <div className="relative aspect-[16/10] rounded-2xl overflow-hidden border border-white/10 bg-black mb-6">
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src="/videos/floorplan.mp4" poster="/videos/floorplan.jpg"
              autoPlay muted loop playsInline preload="metadata"
              data-testid="landing-video-floorplan"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3">
              <span className="card-glass backdrop-blur-md rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider">{t.ui.floor_plan_3d}</span>
            </div>
          </div>
          <ul className="space-y-3">
            {t.immersive.bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-white/80">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#C4A47C]" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          {demoSlug && (
            <Link to={`/p/${demoSlug}`} data-testid="immersive-open-demo"
              className="mt-8 inline-flex items-center gap-2 text-sm gold uppercase tracking-wider hover:text-[#E0C594]">
              {t.hero.secondary} <ArrowRight className="h-4 w-4 flip-icon" />
            </Link>
          )}
        </div>
      </div>
    </Section>
  );
}

// ── Smart Unit Selection ───────────────────────────────────────────────────
function SmartSelection({ t }) {
  return (
    <Section id="smart" testid="landing-smart" eyebrow={t.smart.eyebrow} title={t.smart.title} sub={t.smart.sub}>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div>
          <div className="flex flex-wrap gap-2">
            {t.smart.chips.map((c, i) => (
              <span key={i} className="card-glass rounded-full px-3 py-2 text-xs">{c}</span>
            ))}
          </div>
          <div className="mt-8 card-glass rounded-2xl p-5 space-y-4 max-w-[520px]">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.3em] text-white/50"><span>{t.ui.finder}</span><span className="gold">{t.ui.results}</span></div>
            <FakeRange label={t.ui.price} from="$120k" to="$150k" pct={35} />
            <FakeRange label={t.ui.area}  from="70 m²" to="110 m²" pct={55} />
            <div className="flex items-center gap-2 text-xs pt-1">
              <span className="text-white/50">{t.ui.view}</span>
              <span className="card-glass rounded-full px-2 py-0.5">{t.ui.sea}</span>
              <span className="text-white/50">{t.ui.type}</span>
              <span className="card-glass rounded-full px-2 py-0.5"><bdi dir="ltr">2+1</bdi></span>
              <span className="text-white/50">{t.ui.status}</span>
              <span className="card-glass rounded-full px-2 py-0.5 text-emerald-300">{t.ui.available}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { code: "A-702", tag: `2+1 · ${t.ui.sea}`,    p: "$242,000", a: "104 m²", s: "Available" },
            { code: "B-501", tag: `1+1 · ${t.ui.garden}`, p: "$139,000", a: "72 m²",  s: "Available" },
            { code: "A-905", tag: `3+1 · ${t.ui.sea}`,    p: "$412,000", a: "148 m²", s: "Reserved" },
            { code: "B-303", tag: `1+1 · ${t.ui.city}`,   p: "$121,000", a: "68 m²",  s: "Available" },
          ].map((u) => (
            <div key={u.code} className="card-glass rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50"><bdi>{u.tag}</bdi></div>
              <div className="fnt-display text-lg mt-1"><bdi dir="ltr">{u.code}</bdi></div>
              <div className="text-xs text-white/60 mt-1"><bdi dir="ltr">{u.a}</bdi> · <bdi dir="ltr">{u.p}</bdi></div>
              <div className={`inline-flex mt-2 text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${u.s === "Available" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" : "text-amber-300 border-amber-400/30 bg-amber-400/10"}`}>{u.s === "Available" ? t.ui.available : t.ui.reserved}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function FakeRange({ label, from, to, pct }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-white/70">
        <span>{label}</span><span className="tabular-nums" dir="ltr">{from} — {to}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-white/8 relative">
        <div className="absolute h-full bg-[#C4A47C]" style={{ left: `${pct - 25}%`, width: "25%" }} />
      </div>
    </div>
  );
}

// ── Sales tools ────────────────────────────────────────────────────────────
function SalesTools({ t }) {
  const icons = [Bookmark, GitCompare, FileText, Send, QrCode, ClipboardCheck];
  return (
    <Section id="sales" testid="landing-sales" eyebrow={t.sales.eyebrow} title={t.sales.title} sub={t.sales.sub}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
        {t.sales.items.map((it, i) => {
          const Icon = icons[i] || Sparkles;
          return (
            <div key={i} className="card-glass rounded-2xl p-5 hover:border-[#C4A47C]/25 transition">
              <div className="h-9 w-9 rounded-full bg-[#C4A47C]/10 grid place-items-center border border-[#C4A47C]/25">
                <Icon className="h-4 w-4 gold" />
              </div>
              <div className="fnt-display text-lg mt-4">{it.t}</div>
              <div className="text-white/60 text-sm mt-1.5 leading-relaxed">{it.d}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ── Leads ─────────────────────────────────────────────────────────────────
function Leads({ t }) {
  return (
    <Section id="leads" testid="landing-leads" eyebrow={t.leads.eyebrow} title={t.leads.title} sub={t.leads.sub}>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 items-center">
        <div className="card-glass rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#C4A47C]/10 blur-3xl" />
          <div className="text-[10px] uppercase tracking-[0.3em] gold">{t.ui.new_enquiry}</div>
          <div className="fnt-display text-xl mt-1"><bdi dir="ltr">A-702 · 2+1</bdi> · {t.ui.sea_view}</div>
          <div className="text-white/60 text-sm mt-1">{t.ui.journey}</div>
          <div className="mt-4 space-y-2 text-sm">
            <Row k={t.ui.name} v="Karim A." />
            <Row k={t.ui.phone} v={<bdi dir="ltr">+971 55 000 0000</bdi>} />
            <Row k={t.ui.interest} v={t.ui.payment_example} />
            <Row k={t.ui.language} v={t.ui.english} />
          </div>
          <div className="mt-5 flex gap-2">
            <span className="card-glass rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> {t.ui.whatsapp_ready}</span>
            <span className="card-glass rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider inline-flex items-center gap-1.5"><PhoneCall className="h-3.5 w-3.5" /> {t.ui.call_now}</span>
          </div>
        </div>
        <div>
          <ul className="space-y-3">
            {t.leads.items.map((li) => (
              <li key={li} className="flex items-start gap-3 text-white/80">
                <Check className="mt-1 h-4 w-4 gold" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 text-xs text-white/50">{t.leads.footnote}</div>
        </div>
      </div>
    </Section>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-t border-white/5 pt-2">
      <span className="text-white/50">{k}</span>
      <span className="text-white/90"><bdi>{v}</bdi></span>
    </div>
  );
}

// ── Analytics ─────────────────────────────────────────────────────────────
function Analytics({ t }) {
  return (
    <Section id="analytics" testid="landing-analytics" eyebrow={t.analytics.eyebrow} title={t.analytics.title} sub={t.analytics.sub}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
        {t.analytics.kpis.map((k, i) => (
          <div key={i} className="card-glass rounded-2xl p-5">
            <TrendingUp className="h-4 w-4 gold" />
            <div className="fnt-display text-2xl mt-3 leading-none">{k.k}</div>
            <div className="text-white/55 text-xs mt-2">{k.l}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ── Showroom ──────────────────────────────────────────────────────────────
function Showroom({ t }) {
  return (
    <Section id="showroom" testid="landing-showroom" eyebrow={t.showroom.eyebrow} title={t.showroom.title} sub={t.showroom.sub}>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 items-center">
        <div className="relative aspect-[16/10] rounded-3xl overflow-hidden border border-white/10 bg-black">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/videos/locations.mp4" poster="/videos/locations.jpg"
            autoPlay muted loop playsInline preload="metadata"
            data-testid="landing-video-locations"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/40 to-transparent" />
          <div className="absolute bottom-4 left-4 flex gap-2 flex-wrap">
            {t.showroom.tags.map((tg) => (
              <span key={tg} className="card-glass backdrop-blur-md rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wider">{tg}</span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ShowroomStat icon={Monitor} label={t.ui.showroom} val={t.ui.fullscreen} />
          <ShowroomStat icon={Layers}  label={<span lang="en">iPad</span>} val={t.ui.landscape} />
          <ShowroomStat icon={Building2} label={t.ui.investor} val={t.ui.presentation} />
          <ShowroomStat icon={Sparkles}  label={t.ui.guided} val={t.ui.scenarios} />
        </div>
      </div>
    </Section>
  );
}
function ShowroomStat({ icon: I, label, val }) {
  return (
    <div className="card-glass rounded-2xl p-4">
      <I className="h-4 w-4 gold" />
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mt-3">{label}</div>
      <div className="fnt-display text-lg mt-1">{val}</div>
    </div>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────
function Pricing({ t, lang, onChoose }) {
  return (
    <Section id="pricing" testid="landing-pricing" eyebrow={t.pricing.eyebrow} title={t.pricing.title}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {t.pricing.plans.map((p) => (
          <div key={p.id}
            data-testid={`pricing-card-${p.id}`}
            className={`relative rounded-3xl p-6 flex flex-col ${p.featured ? "bg-[#C4A47C]/[0.06] border-2 border-[#C4A47C]/50 shadow-[0_20px_80px_rgba(196,164,124,0.15)]" : "card-glass"}`}>
            {p.featured && (
              <div className="absolute -top-3 start-6 text-[10px] uppercase tracking-[0.3em] px-2.5 py-1 rounded-full bg-[#C4A47C] text-[#0B0E14]">
                {t.ui.recommended}
              </div>
            )}
            <div className="eyebrow">{p.name}</div>
            <div className="fnt-display text-2xl mt-2">{p.for}</div>
            <div className="mt-5">
              {p.setup != null ? (
                <>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[11px] uppercase tracking-wider text-white/50">{t.pricing.startingAt}</span>
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="fnt-display text-3xl"><bdi>{new Intl.NumberFormat(lang, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(p.setup)}</bdi></span>
                    <span className="text-xs text-white/50">{t.pricing.setup}</span>
                  </div>
                  <div className="text-white/60 text-sm"><bdi>{new Intl.NumberFormat(lang, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(p.month)}</bdi> {t.pricing.month}</div>
                </>
              ) : (
                <div className="fnt-display text-3xl">{p.custom}</div>
              )}
            </div>
            <ul className="mt-5 space-y-2 text-sm text-white/75 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 gold shrink-0" /><span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onChoose(p.id)}
              data-testid={`pricing-choose-${p.id}`}
              className={`mt-6 rounded-full px-5 py-3 text-sm uppercase tracking-wider ${p.featured ? "btn-primary" : "btn-ghost"}`}>
              {p.setup == null ? t.pricing.ctaCustom : t.pricing.cta}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-8 max-w-3xl text-xs text-white/50 leading-relaxed space-y-2" data-testid="pricing-disclaimer">
        <p>{t.pricing.disclaimer1}</p>
        <p>{t.pricing.disclaimer2}</p>
      </div>
    </Section>
  );
}

// ── Final CTA ─────────────────────────────────────────────────────────────
function FinalCta({ t, onBook }) {
  return (
    <section className="relative mt-24 border-y border-white/5" data-testid="landing-finalcta">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <h2 className="fnt-display text-3xl md:text-5xl leading-tight max-w-3xl mx-auto">
          {t.finalCta.title}
        </h2>
        <p className="mt-4 text-white/60 max-w-xl mx-auto">{t.finalCta.sub}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button onClick={onBook} data-testid="finalcta-book"
            className="btn-primary px-6 py-3 rounded-full text-sm uppercase tracking-wider inline-flex items-center gap-2">
            {t.finalCta.primary} <ArrowRight className="h-4 w-4 flip-icon" />
          </button>
          <button onClick={onBook} data-testid="finalcta-contact"
            className="btn-ghost px-6 py-3 rounded-full text-sm uppercase tracking-wider">
            {t.finalCta.secondary}
          </button>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────
function Footer({ t }) {
  return (
    <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-sm text-white/50">
      <div>
        <div className="fnt-display text-white text-lg">{t.footer.brand}</div>
        <div className="mt-1">{t.footer.tagline}</div>
      </div>
      <div className="text-xs">{t.footer.copyright(new Date().getFullYear())}</div>
    </footer>
  );
}

// ── Generic Section wrapper ───────────────────────────────────────────────
function Section({ id, testid, eyebrow, title, sub, children }) {
  return (
    <section id={id} data-testid={testid} className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28">
      <div className="max-w-3xl">
        <div className="eyebrow">{eyebrow}</div>
        <h2 className="fnt-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] mt-4">{title}</h2>
        {sub && <p className="mt-4 text-white/65 text-base md:text-lg leading-relaxed">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

// ── Book-Demo modal ───────────────────────────────────────────────────────
function BookDemoModal({ t, lang, initialPlan, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    units_planned: "", plan_interest: initialPlan || "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm({ ...form, ...patch });
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef, onClose);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    setError(null);
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/marketing/demo-requests", { ...form, language: lang });
      setDone(true);
    } catch (err) {
      setError(t.bookForm.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="book-demo-title" data-testid="book-demo-modal">
      <div className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto bg-[#10141B] border border-white/10 rounded-3xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <div id="book-demo-title" className="eyebrow">{t.bookForm.title}</div>
            {!done && <div className="fnt-display text-lg mt-1">{t.hero.eyebrow}</div>}
          </div>
          <button onClick={onClose} data-testid="book-demo-close" aria-label={t.bookForm.close}
            className="text-white/60 hover:text-white p-1"><X className="h-5 w-5" /></button>
        </div>
        {done ? (
          <div className="p-10 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#C4A47C]/20 text-[#C4A47C] grid place-items-center">
              <Check className="h-6 w-6" />
            </div>
            <div className="fnt-display text-2xl mt-4">{t.bookForm.thanks}</div>
            <div className="text-white/60 text-sm mt-2 max-w-sm mx-auto">{t.bookForm.thanksSub}</div>
            <button onClick={onClose} data-testid="book-demo-done-close"
              className="mt-6 btn-primary rounded-full px-6 py-2.5 text-sm uppercase tracking-wider">
              {t.bookForm.close}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <p className="text-white/60 text-sm">{t.bookForm.sub}</p>
            <div className="grid grid-cols-2 gap-3">
              <F label={t.bookForm.name}>
                <input required autoComplete="name" type="text" value={form.name} onChange={(e) => set({ name: e.target.value })}
                  className="input" data-testid="book-name" />
              </F>
              <F label={t.bookForm.email}>
                <input required autoComplete="email" dir="ltr" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })}
                  className="input" data-testid="book-email" />
              </F>
              <F label={t.bookForm.company}>
                <input autoComplete="organization" type="text" value={form.company} onChange={(e) => set({ company: e.target.value })}
                  className="input" data-testid="book-company" />
              </F>
              <F label={t.bookForm.phone}>
                <input autoComplete="tel" dir="ltr" type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                  className="input" data-testid="book-phone" />
              </F>
              <F label={t.bookForm.units}>
                <select value={form.units_planned} onChange={(e) => set({ units_planned: e.target.value })}
                  className="input" data-testid="book-units">
                  <option value="">—</option>
                  {t.bookForm.unitsOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </F>
              <F label={t.bookForm.plan}>
                <select value={form.plan_interest} onChange={(e) => set({ plan_interest: e.target.value })}
                  className="input" data-testid="book-plan">
                  <option value="">—</option>
                  {t.bookForm.planOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </F>
            </div>
            <F label={t.bookForm.message}>
              <textarea rows={3} value={form.message} onChange={(e) => set({ message: e.target.value })}
                className="input" data-testid="book-message" />
            </F>
            {error && <div role="alert" className="text-xs text-red-300">{error}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-ghost rounded-full px-4 py-2 text-xs uppercase tracking-wider" data-testid="book-cancel">
                {t.bookForm.cancel}
              </button>
              <button type="submit" disabled={submitting}
                className="btn-primary rounded-full px-5 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="book-submit">
                {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.bookForm.submit}</> : t.bookForm.submit}
              </button>
            </div>
          </form>
        )}
      </div>
      <style>{`
        .input { width:100%; background:#0B0E14; border:1px solid rgba(255,255,255,0.08); color:#F5F5F1; padding:0.55rem 0.75rem; border-radius:0.5rem; font-size:0.875rem; }
        .input:focus { outline: 2px solid rgba(196,164,124,0.4); outline-offset: 0; }
      `}</style>
    </div>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/45 mb-1">{label}</div>
      {children}
    </label>
  );
}
